import liff from "@line/liff";
import { clsx } from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import {
  fetchQualificationTypes,
  getErrorMessage,
  type QualificationType,
  registerQualification,
  registerSubjectsBatch,
} from "@/api/client";
import {
  isMaritimeDepartment,
  type MaritimeDepartment,
  type MaritimeGrade,
  useMaritimeQualifications,
} from "@/hooks/useMaritimeQualifications";
import { useUserQualifications } from "@/hooks/useUserQualifications";
import { GradeSelector } from "./GradeSelector";
import { CheckCircleOceanIcon } from "./icons/CheckCircleOceanIcon";
import { OralExamForm } from "./OralExamForm";
import { SubjectCheckboxList } from "./SubjectCheckboxList";
import { BackButton } from "./ui/BackButton";
import { Button } from "./ui/Button";
import { Card } from "./ui/Card";
import { Input } from "./ui/Input";
import { Select } from "./ui/Select";

type Props = {
  userId: string;
};

type FormMode =
  | "select"
  | "maritime-grade"
  | "maritime-subjects"
  | "maritime-oral"
  | "maritime-already-acquired";

const MessageDisplay = ({ type, text }: { type: "success" | "error"; text: string }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className={clsx(
      "mt-4 rounded-xl border p-4 text-center",
      type === "success"
        ? "border-success-border bg-success-bg text-success-text"
        : "border-error-border bg-error-bg text-error-text",
    )}
  >
    {text}
  </motion.div>
);

const LoadingState = () => (
  <Card>
    <div className="flex items-center justify-center py-8">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-ocean-200 border-t-ocean-500" />
      <span className="ml-3 text-ocean-600">読み込み中...</span>
    </div>
  </Card>
);

export const QualificationForm = ({ userId }: Props) => {
  const [qualificationTypes, setQualificationTypes] = useState<QualificationType[]>([]);
  const [qualificationTypeId, setQualificationTypeId] = useState<number | null>(null);
  const [acquiredAt, setAcquiredAt] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const [formMode, setFormMode] = useState<FormMode>("select");
  const [selectedDepartment, setSelectedDepartment] = useState<MaritimeDepartment | null>(null);
  const [selectedGrade, setSelectedGrade] = useState<MaritimeGrade | null>(null);
  const [selectedSubjectIds, setSelectedSubjectIds] = useState<Set<number>>(new Set());

  const {
    isLoading: isLoadingMaritime,
    error: maritimeError,
    checkWrittenExamStatus,
    checkOralExamStatus,
    getTopQualificationId,
    departmentNames,
    gradeNames,
  } = useMaritimeQualifications();
  const {
    qualificationMap,
    isLoading: isLoadingUserQuals,
    error: userQualsError,
  } = useUserQualifications(userId);

  useEffect(() => {
    const load = async () => {
      try {
        const types = await fetchQualificationTypes();
        setQualificationTypes(types);
      } catch (error) {
        console.error("Failed to fetch qualification types:", error);
        setMessage({ type: "error", text: "資格種別の取得に失敗しました" });
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const handleSelectionChange = (value: string) => {
    setMessage(null);
    setAcquiredAt("");

    if (value.startsWith("maritime-")) {
      const department = value.replace("maritime-", "");
      if (!isMaritimeDepartment(department)) {
        return;
      }
      setQualificationTypeId(null);
      setSelectedDepartment(department);
      setSelectedGrade(null);
      setFormMode("maritime-grade");
      return;
    }

    const id = value ? Number(value) : null;
    setQualificationTypeId(id);
    setFormMode("select");
    setSelectedDepartment(null);
    setSelectedGrade(null);
  };

  const handleGradeSelect = (grade: MaritimeGrade) => {
    setSelectedGrade(grade);
    setSelectedSubjectIds(new Set());

    if (selectedDepartment) {
      const oralStatus = checkOralExamStatus(selectedDepartment, grade, qualificationMap);
      if (oralStatus.acquired) {
        setFormMode("maritime-already-acquired");
        return;
      }

      const status = checkWrittenExamStatus(selectedDepartment, grade, qualificationMap);
      if (status?.passed) {
        setFormMode("maritime-oral");
      } else {
        setFormMode("maritime-subjects");
      }
    }
  };

  const handleBack = () => {
    if (
      formMode === "maritime-oral" ||
      formMode === "maritime-subjects" ||
      formMode === "maritime-already-acquired"
    ) {
      setFormMode("maritime-grade");
      setSelectedGrade(null);
      setSelectedSubjectIds(new Set());
    } else if (formMode === "maritime-grade") {
      setFormMode("select");
      setQualificationTypeId(null);
      setSelectedDepartment(null);
    }
    setMessage(null);
    setAcquiredAt("");
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!qualificationTypeId || !acquiredAt) {
      return;
    }

    setIsSubmitting(true);
    setMessage(null);

    try {
      await registerQualification({
        userId,
        qualificationTypeId,
        acquiredAt,
      });
      liff.closeWindow();
    } catch (error) {
      console.error("Failed to register qualification:", error);
      setMessage({ type: "error", text: getErrorMessage(error) });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubjectSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (selectedSubjectIds.size === 0 || !acquiredAt) {
      return;
    }

    setIsSubmitting(true);
    setMessage(null);

    try {
      await registerSubjectsBatch({
        userId,
        qualificationTypeIds: Array.from(selectedSubjectIds),
        acquiredAt,
      });
      liff.closeWindow();
    } catch (error) {
      console.error("Failed to register subjects:", error);
      setMessage({ type: "error", text: getErrorMessage(error) });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (maritimeError || userQualsError) {
    console.error({ maritimeError, userQualsError });
    return (
      <Card variant="error">
        <p className="text-center text-error-text">データの読み込みに失敗しました。</p>
      </Card>
    );
  }

  if (isLoading || isLoadingMaritime || isLoadingUserQuals) {
    return <LoadingState />;
  }

  // 海技士の資格取得済み表示
  if (formMode === "maritime-already-acquired" && selectedDepartment && selectedGrade) {
    const qualificationName = `海技士（${departmentNames[selectedDepartment]}）${gradeNames[selectedGrade]}`;
    const oralStatus = checkOralExamStatus(selectedDepartment, selectedGrade, qualificationMap);
    const acquiredDate =
      oralStatus.acquired && oralStatus.qualification.acquiredAt
        ? new Date(oralStatus.qualification.acquiredAt).toLocaleDateString("ja-JP")
        : "不明";

    return (
      <Card>
        <BackButton onClick={handleBack} />

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mb-6 flex items-center gap-3 rounded-xl border border-info-border bg-info-bg p-4"
        >
          <CheckCircleOceanIcon />
          <div>
            <h3 className="font-semibold text-info-text">資格取得済み</h3>
            <p className="mt-0.5 text-ocean-600 text-sm">{qualificationName}は既に取得済みです</p>
          </div>
        </motion.div>

        <div className="rounded-xl border border-ocean-200 bg-ocean-50/50 p-4">
          <h4 className="mb-2 font-semibold text-ocean-800">{qualificationName}</h4>
          <p className="text-ocean-600 text-sm">取得日: {acquiredDate}</p>
          <p className="mt-2 text-ocean-500 text-xs">
            海技士資格は永久に有効です。再登録の必要はありません。
          </p>
        </div>
      </Card>
    );
  }

  // 海技士の口述試験合格登録フォーム
  if (formMode === "maritime-oral" && selectedDepartment && selectedGrade) {
    const topQualId = getTopQualificationId(selectedDepartment, selectedGrade);
    if (topQualId) {
      return (
        <OralExamForm
          userId={userId}
          department={selectedDepartment}
          grade={selectedGrade}
          topQualificationId={topQualId}
          departmentNames={departmentNames}
          gradeNames={gradeNames}
          onBack={handleBack}
        />
      );
    }
  }

  // 海技士の科目選択フォーム
  if (formMode === "maritime-subjects" && selectedDepartment && selectedGrade) {
    const status = checkWrittenExamStatus(selectedDepartment, selectedGrade, qualificationMap);
    if (status && !status.passed) {
      const selectableSubjects = status.subjects.filter((s) => !s.isDisabled);

      return (
        <Card>
          <BackButton onClick={handleBack} />

          <h2 className="mb-5 font-semibold text-lg text-ocean-800">
            {departmentNames[selectedDepartment]}
            {gradeNames[selectedGrade]} 科目合格登録
          </h2>

          <form onSubmit={handleSubjectSubmit}>
            <SubjectCheckboxList
              subjects={status.subjects}
              selectedIds={selectedSubjectIds}
              onSelectionChange={setSelectedSubjectIds}
            />

            <AnimatePresence mode="wait">
              {selectableSubjects.length === 0 ? (
                <motion.div
                  key="all-valid"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="rounded-xl border border-info-border bg-info-bg p-4 text-center text-info-text"
                >
                  全ての科目が有効期限内です。筆記合格の登録に進んでください。
                </motion.div>
              ) : (
                <motion.div
                  key="form-fields"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <Input
                    type="date"
                    label="合格日"
                    value={acquiredAt}
                    onChange={(e) => setAcquiredAt(e.target.value)}
                    required
                    hint="選択した全ての科目に同じ合格日が適用されます"
                    className="mb-5"
                  />

                  <Button
                    type="submit"
                    variant="line"
                    fullWidth
                    isLoading={isSubmitting}
                    disabled={selectedSubjectIds.size === 0 || !acquiredAt}
                  >
                    {selectedSubjectIds.size}科目を登録する
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>

            {message && <MessageDisplay type={message.type} text={message.text} />}
          </form>
        </Card>
      );
    }
  }

  // 海技士の級選択フォーム
  if (formMode === "maritime-grade" && selectedDepartment) {
    return (
      <Card>
        <BackButton onClick={handleBack} />

        <h2 className="mb-5 font-semibold text-lg text-ocean-800">
          海技士（{departmentNames[selectedDepartment]}）
        </h2>

        <GradeSelector value={selectedGrade} onChange={handleGradeSelect} gradeNames={gradeNames} />
      </Card>
    );
  }

  // 通常の資格選択フォーム
  const filteredTypes = qualificationTypes.filter((t) => {
    if (t.name.startsWith("海技士")) {
      return false;
    }
    return t.parentQualificationId === null;
  });

  const maritimeOptions: { id: string; name: string; category: "deck" | "engine" }[] = [
    { id: "maritime-deck", name: "海技士（航海）", category: "deck" },
    { id: "maritime-engine", name: "海技士（機関）", category: "engine" },
  ];

  const currentSelectValue = qualificationTypeId?.toString() ?? "";

  return (
    <Card>
      <form onSubmit={handleSubmit}>
        <Select
          label="資格種別"
          value={currentSelectValue}
          onChange={(e) => handleSelectionChange(e.target.value)}
          placeholder="選択してください"
          className="mb-5"
        >
          <optgroup label="海技士">
            {maritimeOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.name}
              </option>
            ))}
          </optgroup>
          <optgroup label="その他">
            {filteredTypes.map((qt) => (
              <option key={qt.id} value={qt.id}>
                {qt.name}
              </option>
            ))}
          </optgroup>
        </Select>

        <AnimatePresence>
          {qualificationTypeId && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Input
                type="date"
                label="取得日"
                value={acquiredAt}
                onChange={(e) => setAcquiredAt(e.target.value)}
                required
                className="mb-5"
              />

              <Button type="submit" variant="line" fullWidth isLoading={isSubmitting}>
                登録する
              </Button>
            </motion.div>
          )}
        </AnimatePresence>

        {message && <MessageDisplay type={message.type} text={message.text} />}
      </form>
    </Card>
  );
};
